/**
 * Gallery Slider functionality for Races and Products
 */

import Swiper from 'swiper';
import { Navigation, Mousewheel, Pagination } from 'swiper/modules';

// Store Swiper instances
let raceGallerySwiper = null;
let produitGallerySwiper = null;

/**
 * Initialize Race Mobile Gallery Swiper
 */
export function initRaceMobileSwiper() {
    const raceGallerySliders = document.querySelectorAll('[data-race-gallery-slider]');

    if (!raceGallerySliders.length) return;

    raceGallerySliders.forEach(slider => {
        if (window.innerWidth < 640) {
            raceGallerySwiper = new Swiper(slider.querySelector('.swiper'), {
                modules: [Navigation, Mousewheel, Pagination],
                slidesPerView: 'auto',
                slidesOffsetBefore: 10,
                slidesOffsetAfter: 10,
                spaceBetween: 10,
                centeredSlides: false,
                loop: false,
                simulateTouch: true,
                allowTouchMove: true,
                grabCursor: true,
                touchEventsTarget: 'container',
                touchMoveStopPropagation: true,
                pagination: {
                    el: slider.querySelector('.swiper-pagination'),
                    clickable: true,
                    bulletActiveClass: 'bg-bleu-moyen',
                    bulletClass: 'swiper-pagination-bullet bg-blanc-30 w-2 h-2 rounded-full mx-1 cursor-pointer transition-all duration-300'
                },
                mousewheel: {
                    forceToAxis: true,
                    sensitivity: 1,
                    releaseOnEdges: true,
                },
            });
        }
    });
}

/**
 * Initialize Product Mobile Gallery Swiper
 */
export function initProduitMobileSwiper() {
    const produitGallerySliders = document.querySelectorAll('[data-produit-gallery-slider]');
    
    if (!produitGallerySliders.length) return;
    
    produitGallerySliders.forEach(slider => {
        if (window.innerWidth < 640) {
            produitGallerySwiper = new Swiper(slider.querySelector('.swiper'), {
                modules: [Navigation, Mousewheel, Pagination],
                slidesPerView: 'auto',
                slidesOffsetBefore: 10,
                slidesOffsetAfter: 10,
                spaceBetween: 10,
                centeredSlides: false,
                loop: false,
                simulateTouch: true,
                allowTouchMove: true,
                grabCursor: true,
                touchEventsTarget: 'container',
                touchMoveStopPropagation: true,
                pagination: {
                    el: slider.querySelector('.swiper-pagination'),
                    clickable: true,
                    bulletActiveClass: 'bg-rouge',
                    bulletClass: 'swiper-pagination-bullet bg-blanc-30 w-2 h-2 rounded-full mx-1 cursor-pointer transition-all duration-300'
                },
                mousewheel: {
                    forceToAxis: true,
                    sensitivity: 1,
                    releaseOnEdges: true,
                },
            });
        }
    });
}

/**
 * Attach Race Thumbnail Events (Desktop)
 */
export function attachRaceThumbnailEvents() {
    const thumbnailButtons = document.querySelectorAll('#race-thumbnails .thumbnail-btn');
    thumbnailButtons.forEach(button => {
        button.addEventListener('click', function () {
            const imageUrl = this.dataset.imageUrl;
            const imageAlt = this.dataset.imageAlt;
            const mainImage = document.getElementById('race-main-image');

            if (mainImage) {
                // Add fade out effect with same timing as race switching
                mainImage.style.transition = 'opacity 0.6s ease-in-out';
                mainImage.style.opacity = '0';
                
                setTimeout(() => {
                    mainImage.src = imageUrl;
                    mainImage.alt = imageAlt;
                    // Fade in after image change
                    mainImage.style.opacity = '1';
                }, 600);

                // Update active state
                thumbnailButtons.forEach(btn => {
                    btn.classList.remove('border-bleu-moyen');
                    btn.classList.add('border-transparent');
                });
                this.classList.remove('border-transparent');
                this.classList.add('border-bleu-moyen');
            }
        });
    });
}

/**
 * Attach Product Thumbnail Events (Desktop)
 */
export function attachProduitThumbnailEvents() {
    const thumbnailButtons = document.querySelectorAll('#produit-thumbnails .thumbnail-btn');
    thumbnailButtons.forEach(button => {
        button.addEventListener('click', function () {
            const imageUrl = this.dataset.imageUrl;
            const imageAlt = this.dataset.imageAlt;
            const mainImage = document.getElementById('produit-main-image');

            if (mainImage) {
                // Add fade out effect with same timing as product switching
                mainImage.style.transition = 'opacity 0.6s ease-in-out';
                mainImage.style.opacity = '0';
                
                setTimeout(() => {
                    mainImage.src = imageUrl;
                    mainImage.alt = imageAlt;
                    // Fade in after image change
                    mainImage.style.opacity = '1';
                }, 600);

                // Update active state
                thumbnailButtons.forEach(btn => {
                    btn.classList.remove('border-rouge');
                    btn.classList.add('border-transparent');
                });
                this.classList.remove('border-transparent');
                this.classList.add('border-rouge');
            }
        });
    });
}

/**
 * Initialize Race Gallery
 */
export function initRaceGallery() {
    // Get data from data attributes
    const dataElement = document.getElementById('races-page-data');
    if (!dataElement) return;

    const racesData = JSON.parse(dataElement.dataset.races || '{}');
    let currentRaceId = parseInt(dataElement.dataset.currentRaceId || '0');

    const raceButtons = document.querySelectorAll('[data-race-id]');

    // Initialize events for initial load
    attachRaceThumbnailEvents();
    initRaceMobileSwiper();

    // Check URL params
    const urlParams = new URLSearchParams(window.location.search);
    const selectedRaceSlug = urlParams.get('race');

    if (selectedRaceSlug) {
        const selectedRaceData = Object.values(racesData).find(race => race.slug === selectedRaceSlug);
        if (selectedRaceData && selectedRaceData.id !== currentRaceId) {
            const targetButton = document.querySelector(`[data-race-id="${selectedRaceData.id}"]`);
            if (targetButton) {
                setTimeout(() => {
                    targetButton.click();
                }, 100);
            }
        }
    }

    // Handle race filter clicks
    raceButtons.forEach(button => {
        button.addEventListener('click', function (e) {
            e.preventDefault();

            const raceId = this.dataset.raceId;
            const raceSlug = this.dataset.raceSlug;

            // Update URL with slug
            const newUrl = new URL(window.location);
            newUrl.searchParams.set('race', raceSlug);
            window.history.pushState({ raceId: raceId }, '', newUrl);

            // Don't reload if same race
            if (parseInt(raceId) === currentRaceId) return;

            // Update active button
            raceButtons.forEach(btn => btn.classList.remove('active'));
            this.classList.add('active');

            // Fade out and update content
            updateRaceContent(raceId, racesData);
            currentRaceId = parseInt(raceId);
        });
    });

    // Initial fade in effect
    initFadeIn('race-content', 'race-map-container');
}

/**
 * Initialize Product Gallery
 */
export function initProductGallery() {
    // Get data from data attributes
    const dataElement = document.getElementById('produits-page-data');
    if (!dataElement) return;

    const produitsData = JSON.parse(dataElement.dataset.produits || '{}');
    let currentProduitId = parseInt(dataElement.dataset.currentProduitId || '0');

    const produitButtons = document.querySelectorAll('[data-produit-id]');

    // Initialize events for initial load
    attachProduitThumbnailEvents();
    initProduitMobileSwiper();

    // Check URL params
    const urlParams = new URLSearchParams(window.location.search);
    const selectedProduitSlug = urlParams.get('produit');

    if (selectedProduitSlug) {
        const selectedProduitData = Object.values(produitsData).find(produit => produit.slug === selectedProduitSlug);
        if (selectedProduitData && selectedProduitData.id !== currentProduitId) {
            const targetButton = document.querySelector(`[data-produit-id="${selectedProduitData.id}"]`);
            if (targetButton) {
                setTimeout(() => {
                    targetButton.click();
                }, 100);
            }
        }
    }

    // Handle product filter clicks
    produitButtons.forEach(button => {
        button.addEventListener('click', function (e) {
            e.preventDefault();

            const produitId = this.dataset.produitId;
            const produitSlug = this.dataset.produitSlug;

            // Update URL
            const newUrl = new URL(window.location);
            newUrl.searchParams.set('produit', produitSlug);
            window.history.pushState({ produitId: produitId }, '', newUrl);

            // Don't reload if same product
            if (parseInt(produitId) === currentProduitId) return;

            // Update active button
            produitButtons.forEach(btn => btn.classList.remove('active'));
            this.classList.add('active');

            // Fade out and update content
            updateProduitContent(produitId, produitsData);
            currentProduitId = parseInt(produitId);
        });
    });

    // Initial fade in effect
    initFadeIn('produit-content', 'produit-map-container');
}

/**
 * Update Race Content
 */
function updateRaceContent(raceId, racesData) {
    const content = document.getElementById('race-content');
    const mapContainer = document.getElementById('race-map-container');

    content.style.transition = 'opacity 0.6s ease-in-out';
    mapContainer.style.transition = 'opacity 0.6s ease-in-out';

    content.style.opacity = '0';
    mapContainer.style.opacity = '0';

    setTimeout(() => {
        const raceData = racesData[raceId];

        // Update gallery
        updateRaceGallery(raceData);

        // Update title
        const titleElement = document.getElementById('race-title');
        if (titleElement) {
            titleElement.textContent = raceData.title;
        }

        // Update products
        updateRaceProducts(raceData);

        // Update characteristics
        updateRaceCharacteristics(raceData);
        
        // Update statistics
        updateRaceStatistics(raceData);

        // Update map
        if (raceData.map) {
            mapContainer.innerHTML = '<img src="' + raceData.map + '" alt="Carte ' + raceData.title + '" class="w-full h-full" id="race-map">';
        } else {
            mapContainer.innerHTML = '';
        }

        // Fade in
        content.style.opacity = '1';
        mapContainer.style.opacity = '1';
    }, 600);
}

/**
 * Update Product Content
 */
function updateProduitContent(produitId, produitsData) {
    const content = document.getElementById('produit-content');
    const mapContainer = document.getElementById('produit-map-container');

    content.style.transition = 'opacity 0.6s ease-in-out';
    mapContainer.style.transition = 'opacity 0.6s ease-in-out';

    content.style.opacity = '0';
    mapContainer.style.opacity = '0';

    setTimeout(() => {
        const produitData = produitsData[produitId];

        // Update gallery
        updateProduitGallery(produitData);

        // Update title
        document.getElementById('produit-title').textContent = produitData.title;

        // Update races
        updateProduitRaces(produitData);

        // Update description
        updateProduitDescription(produitData);

        // Update map
        if (produitData.map) {
            mapContainer.innerHTML = '<img src="' + produitData.map + '" alt="Carte ' + produitData.title + '" class="w-full h-full" id="produit-map">';
        } else {
            mapContainer.innerHTML = '';
        }

        // Fade in
        content.style.opacity = '1';
        mapContainer.style.opacity = '1';
    }, 600);
}

/**
 * Helper Functions
 */

function updateRaceGallery(raceData) {
    // Update desktop gallery
    const galleryContainer = document.getElementById('race-gallery-container');
    if (galleryContainer) {
        let galleryHtml = '<div class="relative 2xl:h-[500px] 2xl:w-[500px] xl:h-[450px] xl:w-[450px] lg:h-[350px] lg:w-[350px] h-[500px] w-[500px] rounded-b-[10px] overflow-hidden flex-shrink-0">';
        galleryHtml += '<img src="' + raceData.gallery[0].url + '" alt="' + raceData.gallery[0].alt + '" class="w-full h-full object-cover" id="race-main-image" style="transition: opacity 0.6s ease-in-out;">';
        galleryHtml += '</div>';

        if (raceData.gallery.length > 1) {
            galleryHtml += '<div class="flex flex-col justify-center gap-1 overflow-y-auto no-scrollbar py-2" id="race-thumbnails">';
            raceData.gallery.forEach((image, index) => {
                galleryHtml += '<button class="thumbnail-btn rounded-md overflow-hidden border-2 ' + (index === 0 ? 'border-bleu-moyen' : 'border-transparent') + '" ';
                galleryHtml += 'data-image-url="' + image.url + '" data-image-alt="' + image.alt + '" data-index="' + index + '">';
                galleryHtml += '<img src="' + image.url + '" alt="' + image.alt + '" class="2xl:w-[5.5rem] 2xl:h-[5.5rem] xl:w-20 xl:h-20 lg:w-[60px] lg:h-[60px] w-[5.5rem] h-[5.5rem] object-cover rounded-md hover:opacity-75 transition-opacity">';
                galleryHtml += '</button>';
            });
            galleryHtml += '</div>';
        }

        galleryContainer.innerHTML = galleryHtml;
        galleryContainer.className = 'hidden sm:flex gap-3 mb-4';
    }

    // Update mobile gallery
    const mobileGallery = document.getElementById('race-mobile-gallery');
    if (mobileGallery) {
        let mobileHtml = '<div class="swiper"><div class="swiper-wrapper">';
        raceData.gallery.forEach((image) => {
            mobileHtml += '<div class="swiper-slide">';
            mobileHtml += '<div class="w-[80vw] h-[80vw] rounded-[10px] overflow-hidden">';
            mobileHtml += '<img src="' + image.url + '" alt="' + image.alt + '" class="w-full h-full object-cover">';
            mobileHtml += '</div></div>';
        });
        mobileHtml += '</div><div class="swiper-pagination mt-3"></div></div>';
        mobileGallery.innerHTML = mobileHtml;
    }

    // Reinitialize events
    attachRaceThumbnailEvents();
    if (raceGallerySwiper) {
        raceGallerySwiper.destroy();
    }
    initRaceMobileSwiper();
}

function updateProduitGallery(produitData) {
    // Update desktop gallery
    const galleryContainer = document.getElementById('produit-gallery-container');
    if (galleryContainer) {
        let galleryHtml = '<div class="relative 2xl:h-[500px] 2xl:w-[500px] xl:h-[450px] xl:w-[450px] lg:h-[350px] lg:w-[350px] h-[500px] w-[500px] rounded-b-[10px] overflow-hidden flex-shrink-0">';
        galleryHtml += '<img src="' + produitData.gallery[0].url + '" alt="' + produitData.gallery[0].alt + '" class="w-full h-full object-cover" id="produit-main-image" style="transition: opacity 0.6s ease-in-out;">';
        galleryHtml += '</div>';

        if (produitData.gallery.length > 1) {
            galleryHtml += '<div class="flex flex-col justify-center gap-1 overflow-y-auto no-scrollbar py-2" id="produit-thumbnails">';
            produitData.gallery.forEach((image, index) => {
                galleryHtml += '<button class="thumbnail-btn rounded-md overflow-hidden border-2 ' + (index === 0 ? 'border-rouge' : 'border-transparent') + '" ';
                galleryHtml += 'data-image-url="' + image.url + '" data-image-alt="' + image.alt + '" data-index="' + index + '">';
                galleryHtml += '<img src="' + image.url + '" alt="' + image.alt + '" class="2xl:w-[5.5rem] 2xl:h-[5.5rem] xl:w-20 xl:h-20 lg:w-[60px] lg:h-[60px] w-[5.5rem] h-[5.5rem] object-cover rounded-md hover:opacity-75 transition-opacity">';
                galleryHtml += '</button>';
            });
            galleryHtml += '</div>';
        }

        galleryContainer.innerHTML = galleryHtml;
        galleryContainer.className = 'hidden sm:flex gap-3 mb-4';
    }

    // Update mobile gallery
    const mobileGallery = document.getElementById('produit-mobile-gallery');
    if (mobileGallery) {
        let mobileHtml = '<div class="swiper"><div class="swiper-wrapper">';
        produitData.gallery.forEach((image) => {
            mobileHtml += '<div class="swiper-slide">';
            mobileHtml += '<div class="w-[80vw] h-[80vw] rounded-[10px] overflow-hidden">';
            mobileHtml += '<img src="' + image.url + '" alt="' + image.alt + '" class="w-full h-full object-cover">';
            mobileHtml += '</div></div>';
        });
        mobileHtml += '</div><div class="swiper-pagination mt-3"></div></div>';
        mobileGallery.innerHTML = mobileHtml;
    }

    // Reinitialize events
    attachProduitThumbnailEvents();
    if (produitGallerySwiper) {
        produitGallerySwiper.destroy();
    }
    initProduitMobileSwiper();
}

function updateRaceProducts(raceData) {
    const productsContainer = document.getElementById('race-products');
    if (raceData.products_html) {
        if (productsContainer) {
            productsContainer.innerHTML = raceData.products_html;
        } else {
            document.getElementById('race-details').innerHTML =
                '<p class="menu uppercase mb-2">Produits disponibles :</p>' +
                '<div class="flex flex-wrap flex-shrink-0 gap-2 mb-8" id="race-products">' +
                raceData.products_html + '</div>' +
                document.getElementById('race-details').innerHTML;
        }
    } else if (productsContainer) {
        if (productsContainer.previousElementSibling) {
            productsContainer.previousElementSibling.remove();
        }
        productsContainer.remove();
    }
}

function updateRaceCharacteristics(raceData) {
    const characteristicsContainer = document.getElementById('race-characteristics');
    if (raceData.characteristics) {
        if (characteristicsContainer) {
            characteristicsContainer.innerHTML = raceData.characteristics;
        } else {
            const detailsDiv = document.getElementById('race-details');
            const characteristicsHtml = '<div class="caracteristiques p2-l  wysiwyg" id="race-characteristics">' +
                raceData.characteristics + '</div>';
            detailsDiv.insertAdjacentHTML('beforeend', characteristicsHtml);
        }
    } else if (characteristicsContainer) {
        characteristicsContainer.remove();
    }
}

function updateRaceStatistics(raceData) {
    const statisticsContainer = document.getElementById('race-statistics');
    if (raceData.chiffres && raceData.chiffres.length > 0) {
        let statisticsHtml = '';
        raceData.chiffres.forEach((chiffre, index) => {
            if (index > 0) {
                statisticsHtml += '<hr class="my-6 border-t border-blanc ">';
            }
            statisticsHtml += '<div class="wysiwyg text-blanc capital16">';
            statisticsHtml += chiffre.chiffre_content || '';
            statisticsHtml += '</div>';
        });
        
        if (statisticsContainer) {
            statisticsContainer.innerHTML = statisticsHtml;
        } else {
            const detailsDiv = document.getElementById('race-details');
            const statisticsContainerHtml = '<div class="my-16 px-8 py-4 flou bg-grey-20 rounded-md" id="race-statistics">' +
                statisticsHtml + '</div>';
            detailsDiv.insertAdjacentHTML('beforeend', statisticsContainerHtml);
        }
    } else if (statisticsContainer) {
        statisticsContainer.remove();
    }
}

function updateProduitRaces(produitData) {
    const racesContainer = document.getElementById('produit-races');
    if (produitData.races_html) {
        if (racesContainer) {
            racesContainer.innerHTML = produitData.races_html;
        } else {
            document.getElementById('produit-details').innerHTML =
                '<p class="menu uppercase mb-2">Races disponibles :</p>' +
                '<div class="flex flex-wrap flex-shrink-0 gap-2 mb-8" id="produit-races">' +
                produitData.races_html + '</div>' +
                document.getElementById('produit-details').innerHTML;
        }
    } else if (racesContainer) {
        if (racesContainer.previousElementSibling) {
            racesContainer.previousElementSibling.remove();
        }
        racesContainer.remove();
    }
}

function updateProduitDescription(produitData) {
    const descriptionContainer = document.getElementById('produit-description');
    if (produitData.description) {
        if (descriptionContainer) {
            descriptionContainer.innerHTML = produitData.description;
        } else {
            const detailsDiv = document.getElementById('produit-details');
            const descriptionHtml = '<div class="caracteristiques p2-l lg:pb-16 wysiwyg" id="produit-description">' +
                produitData.description + '</div>';
            detailsDiv.insertAdjacentHTML('beforeend', descriptionHtml);
        }
    } else if (descriptionContainer) {
        descriptionContainer.remove();
    }
}

function initFadeIn(contentId, mapId) {
    const content = document.getElementById(contentId);
    const mapContainer = document.getElementById(mapId);

    if (content && mapContainer) {
        content.style.opacity = '0';
        mapContainer.style.opacity = '0';
        content.style.transition = 'opacity 0.6s ease-in-out';
        mapContainer.style.transition = 'opacity 0.6s ease-in-out';

        setTimeout(() => {
            content.style.opacity = '1';
            mapContainer.style.opacity = '1';
        }, 100);
    }
}

